import {isPlatformBrowser, isPlatformServer} from '@angular/common';
import {HttpClient} from '@angular/common/http';
import {Inject, Injectable, OnDestroy, PLATFORM_ID} from '@angular/core';
import * as dayjs from 'dayjs';
import 'dayjs/locale/sv';
import {CookieService} from 'ngx-cookie-service';
import {BehaviorSubject, forkJoin, Observable, of, ReplaySubject} from 'rxjs';
import {catchError, map, tap} from 'rxjs/operators';
import {CancelBookingStatuses} from '../enums/MyPagesEnums';
import {BookingDetails, CancelBookingResponse, IsAdminResponse, Profile, ProfileUpdate} from '../types/MyPages';
import {ApiHelper} from './api.helper';
import {AppConfigService} from './app-config.service';
import {BankidService} from './bankid.service';

@Injectable({
  providedIn: 'root',
})
export class MyPagesService implements OnDestroy {
  public bookings$: BehaviorSubject<BookingDetails[]> = new BehaviorSubject(null);
  public profile$: ReplaySubject<Profile> = new ReplaySubject();
  public isAdmin$: ReplaySubject<IsAdminResponse> = new ReplaySubject();
  public isLoggedIn$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  private readonly membersUrl: string;
  private isFirstLoad: boolean = true;
  private subscriptions = [];

  constructor(
    private appConfigService: AppConfigService,
    private http: HttpClient,
    private cookieService: CookieService,
    private apiHelper: ApiHelper,
    private bankIdService: BankidService,
    @Inject(PLATFORM_ID) private platformId: any,
  ) {
    this.membersUrl = `${this.appConfigService.appConfig.apiUrl}members/`;
    dayjs.locale('sv');

    if (isPlatformBrowser(this.platformId)) {
      setTimeout(() => {
        this.subscriptions.push(this.getProfile().subscribe());
      }, 4500);
    }

    this.subscriptions.push(
      this.isLoggedIn$.subscribe((res) => {
        if (res === true) {
          this.reloadBookings();
        }
      }),
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }

  public checkLogin(): Observable<boolean> {
    return this.isLoggedIn$.asObservable();
  }

  public logout(): Observable<unknown> {
    return this.bankIdService.logout().pipe(
      tap(() => {
        this.isLoggedIn$.next(false);
        this.profile$.next(null);
        this.isAdmin$.next({isAdmin: false, pnoHash: null});
        this.bookings$.next([]);
      }),
    );
  }

  public filterFutureBookings(bookings: BookingDetails[]) {
    if (bookings) {
      return bookings.filter((booking) => {
        return dayjs(booking.timeSlot).isAfter(dayjs());
      });
    } else {
      return [];
    }
  }

  public login(sessionId: string): Observable<unknown> {
    const vardenDeviceId = this.cookieService.get('tid') || 'd1';

    return this.http.post(
      `${this.membersUrl}mypages/login`,
      {
        session_id: sessionId,
        device_id: vardenDeviceId,
      },
      {withCredentials: true},
    );
  }

  public getProfile(): Observable<Profile> {
    if (isPlatformServer(this.platformId)) {
      return of(null);
    }

    return this.http
      .get<Profile>(`${this.membersUrl}mypages/profile`, {
        withCredentials: true,
      })
      .pipe(
        tap((profile) => {
          this.isLoggedIn$.next(profile !== null);
          this.profile$.next(profile);
        }),
        catchError((err, caught) => {
          this.isLoggedIn$.next(false);
          this.profile$.next(null);
          this.apiHelper.handleError(err);
          return of(null);
        }),
      );
  }

  public getAdminStatus(loggingInToAdmin = false) {
    const notAdmin = {isAdmin: false, pnoHash: null};
    if (!loggingInToAdmin) {
      // We are not logging in to admin, so we dont care
      this.isAdmin$.next(notAdmin);
      return of(notAdmin);
    }
    return this.http
      .get<IsAdminResponse>(`${this.membersUrl}mypages/profile/isadmin`, {
        withCredentials: true,
      })
      .pipe(
        tap((data) => {
          this.isAdmin$.next(data);
        }),
        catchError((err, caught) => {
          this.isAdmin$.next(notAdmin);
          this.apiHelper.handleError(err);
          return of(null);
        }),
      );
  }

  public putProfile(profile: ProfileUpdate): Observable<Profile> {
    return this.http
      .put<Profile>(`${this.membersUrl}mypages/profile`, profile, {
        withCredentials: true,
      })
      .pipe(
        tap((profile) => {
          this.profile$.next(profile);
        }),
        catchError((err) => {
          this.apiHelper.handleError(err);
          return of(null);
        }),
      );
  }

  public getBookings(): Observable<string[]> {
    return this.http
      .get<string[]>(`${this.appConfigService.appConfig.apiUrl}mypages/booking`, {
        withCredentials: true,
        params: {
          t: new Date().getTime().toString(),
        },
      })
      .pipe(
        tap((bookings) => {
          this.runBookingDetailsUpdate(bookings);
        }),
        catchError((err) => {
          return of(null);
        }),
      );
  }

  public getBookingDetails(booking_id: string): Observable<BookingDetails> {
    // return of(this.mockedBooking()).pipe(
    //   map((booking: BookingDetails): BookingDetails => {
    //     return {
    //       ...booking,
    //       time: dayjs(booking.timeSlot).format('ddd YYYY-MM-DD HH:mm'),
    //       date: dayjs(booking.timeSlot).format('YYYY-MM-DD'),
    //     };
    //   }),
    // );

    return this.http
      .get<BookingDetails>(`${this.appConfigService.appConfig.apiUrl}mypages/booking/${booking_id}`, {
        withCredentials: true,
      })
      .pipe(
        catchError((e) => {
          return of({error: true});
        }),
        map((booking: BookingDetails): BookingDetails => {
          return {
            ...booking,
            time: dayjs(booking.timeSlot).format('ddd YYYY-MM-DD HH:mm'),
            date: dayjs(booking.timeSlot).format('YYYY-MM-DD'),
          };
        }),
      );
  }

  public cancelBooking(booking_id: string, reason?: string): Observable<CancelBookingResponse> {
    const apiUrl = this.appConfigService.appConfig.apiUrl;

    return this.http.post<CancelBookingResponse>(
      `${apiUrl}bookingsystem/cancelbooking`,
      {
        bookingId: booking_id,
        message: reason,
      },
      {
        withCredentials: true,
      },
    );
  }

  public getBookingCancellationErrorMessage(status: CancelBookingStatuses): string {
    switch (status) {
      case CancelBookingStatuses.MemberIdIsEmpty:
        return 'Medlemsnummer saknas.';
      case CancelBookingStatuses.MemberPnoCantBeParsedToValidSpin:
        return 'Personnummer kunde inte tolkas.';
      case CancelBookingStatuses.CouldNotFetchBooking:
        return 'Kunde inte hämta bokningen.';
      case CancelBookingStatuses.BookingWithUnCancelableStatus:
        return 'Bokningen har en status som inte kan avbokas.';
      case CancelBookingStatuses.BookingTimeSlotInvalid:
        return 'Bokningens tidsintervall är ogiltigt.';
      case CancelBookingStatuses.BookingStartTimeTooClose:
        return 'Bokningen kan inte avbokas mindre än 24 timmar innan start.';
      case CancelBookingStatuses.CouldNotFetchCustomerRegistration:
        return 'Kunde inte hämta kundens registrering.';
      case CancelBookingStatuses.CustomerDoesNotAllowCancellation:
        return 'Kunden tillåter inte avbokning.';
      case CancelBookingStatuses.FailedToCancelBooking:
      case CancelBookingStatuses.FailedToCancelBookingWithException:
      case CancelBookingStatuses.Cancelled:
      case CancelBookingStatuses.CouldNotFetchMinCancelationTime:
      default:
        return 'Det kan bero på att du redan har avbokat den via telefon hos vårdgivaren.';
    }
  }

  public registerAsAdmin(adminRegistrationToken: string) {
    return this.http
      .post(`${this.membersUrl}mypages/profile/register_admin?token=${adminRegistrationToken}`, null, {
        withCredentials: true,
      })
      .pipe(
        catchError((err) => {
          return of(null);
        }),
      );
  }

  private runBookingDetailsUpdate(bookingIds: string[]): void {
    const observables: Array<Observable<BookingDetails>> = [];
    if (bookingIds && bookingIds.length > 0) {
      bookingIds.forEach((bookingId) => {
        observables.push(this.getBookingDetails(bookingId));
      });
      forkJoin(observables).subscribe((result) => this.processBookingDetails(result));
    } else {
      this.bookings$.next([]);
    }
  }

  private processBookingDetails(bookings: BookingDetails[]): void {
    this.bookings$.next(bookings.filter((booking) => booking.error !== true));
  }

  // private mockedBooking(): BookingDetails {
  //   const booking: BookingDetails = {
  //     reservationId: {
  //       id: '123abc',
  //     },
  //     customer: {
  //       id: '444',
  //       cancellationEnabled: true,
  //     },
  //     canBeCancelled: true,
  //     cancellationReasonRequired: true,
  //     timeSlot: '2024-05-01 10:30',
  //     treatment: {
  //       name: 'Treatment name',
  //       configuration: {
  //         price: '40000 kr',
  //         duration: '120',
  //       },
  //     },
  //     unit: {
  //       id: 18464,
  //       name: 'Gynmottagning Eva Svanborg',
  //       path: 'gynmottagning-eva-svanborg',
  //       logoUrl: 'organizations/organization-18464/gynekolog-obstetriker-eva-svanborg.webp',
  //       address: {
  //         street: 'Centralvägen 17',
  //         zipCode: '11222',
  //         city: 'Solna',
  //       },
  //       contactInfo: {
  //         phoneNumber: '123123123',
  //         webSiteUrl: '',
  //       },
  //     },
  //   };
  //   return booking;

  // }

  private reloadBookings(): void {
    this.subscriptions.push(this.getBookings().subscribe());
  }

  private updateIsAdminStatus(): void {
    this.subscriptions.push(this.getAdminStatus().subscribe());
  }
}
